import React from 'react';
import { twMerge } from 'tailwind-merge';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import { likeChatPost, unlikeChatPost } from 'flow/actions/chat-actions';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { Message } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';

import { TypedMap } from 'flow/types/TypedMap';
import ChatPostLikedList from './ChatPostLikedList';

interface Props {
    message: TypedMap<Message>;
    sessionKey: ChatSessionKey;
    navigateOnClick?: boolean;
    goToComment?: () => void;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

const ChatPostFooter = ({
    message: originalMessage,
    sessionKey,
    navigateOnClick = false,
    goToComment,
    videoDark = false,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    // We may be displaying the post in a modal, which is in the wrong place in the component tree to
    // get updates, so we need to set up a selector to get the latest message changes, such as likes
    // and comment count.
    const latestMessages = useAppSelector((state) =>
        state.chats.getIn(['messages', sessionKey]),
    );

    const message =
        latestMessages?.find(
            (m) => m.get('key') === originalMessage.get('key'),
        ) || originalMessage;

    const ownChatUserKey = useAppSelector((state) =>
        state.app.getIn(['extension', 'chatUserKey']),
    );
    const isLikedByMe = message
        .get('likedBy')
        ?.some((liker) => liker.get('key') === ownChatUserKey);

    const commentCount = message.get('chatComments')?.count();
    const commentCountText = (
        <span className="text-main text-sm font-medium">
            {commentCount === 1
                ? i18next.t('chat.post.comments.comment_count')
                : i18next.t('chat.post.comments.comment_counts', {
                      commentCount,
                  })}
        </span>
    );

    const handleUnlike = () =>
        dispatch(
            unlikeChatPost(sessionKey, message.get('key'), ownChatUserKey),
        );
    const handleLike = () =>
        dispatch(
            likeChatPost(sessionKey, message.get('key'), [
                { chatUserKey: ownChatUserKey },
            ]),
        );

    const openPostModal = (
        e:
            | React.MouseEvent<HTMLDivElement, MouseEvent>
            | React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ): void => {
        openModal('chatPost', { message, sessionKey });
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <div
            className={twMerge(
                'flex flex-row items-center gap-x-4 border-t px-8 py-6 md:px-6',
                videoDark ? 'border-gray-600' : '',
            )}
        >
            <ChatPostLikedList message={message} sessionKey={sessionKey} />

            {navigateOnClick ? (
                <button onClick={openPostModal}>{commentCountText}</button>
            ) : (
                <div>{commentCountText}</div>
            )}

            <div className="flex-1" />
            {goToComment && (
                <IconButton
                    aria-label={i18next.t('action.comment')}
                    onClick={goToComment}
                    className={videoDark ? 'text-gray-300' : 'text-gray-600'}
                >
                    <Icon iconKey="message_outline" />
                </IconButton>
            )}
            <IconButton
                aria-label={i18next.t('action.like')}
                aria-pressed={isLikedByMe}
                onClick={isLikedByMe ? handleUnlike : handleLike}
                className={videoDark ? 'text-gray-300' : 'text-gray-600'}
            >
                <Icon iconKey={isLikedByMe ? 'thumbUp' : 'thumbUp_outline'} />
            </IconButton>
        </div>
    );
};

export default ChatPostFooter;
